/**
 * Bridge Club API Service
 *
 * SECURITY: Uses httpOnly cookies for authentication.
 * Tokens are NOT stored in localStorage to prevent XSS token theft.
 */

import { API_BASE_URL } from './api';

const API_BASE = `${API_BASE_URL}/bridge-club`;

const getHeaders = (): Record<string, string> => {
  // Auth is via httpOnly cookies, no Authorization header needed for browser requests
  return {
    'Content-Type': 'application/json',
  };
};

const handleResponse = async (res: Response) => {
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Request failed' }));
    // CARRY THE STATUS. A gateway timeout from DigitalOcean's edge returns HTML,
    // not JSON, so the parsed body is the 'Request failed' placeholder and tells
    // a caller nothing. Whether the server might STILL be working on the request
    // - 502/503/504 - versus having answered it for good is only knowable from
    // the status code, and a caller that has to decide whether retrying is safe
    // needs it. Assigned rather than wrapped so `err.error` still reads.
    throw Object.assign(error, { status: res.status });
  }
  return res.json();
};

export interface RegisterData {
  email: string;
  password?: string;  // Optional - set after email verification
  first_name: string;
  last_name: string;
  phone?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  home_location_id?: number;  // RetailLocation ID from store finder
  linked_store_id?: number;   // Legacy: Customer ID (deprecated)
  partner_loyalty_id?: string;  // Optional loyalty ID at selected store
  terms_accepted: boolean;
  privacy_accepted: boolean;
  is_local_signup?: boolean;  // True for Kettle Falls mill portal signups
}

export interface AnimalData {
  category: string;
  animal_type: string;
  name?: string;
  breed?: string;
  count?: number;
  weight_lbs?: number;
  age_months?: number;
  life_stage?: string;
  activity_level?: string;
  feed_product_id?: number;
  feed_product_name?: string;
  is_red_bridge_feed?: boolean;
  daily_consumption_lbs?: number;
  notes?: string;
}

export interface ExtractedReceiptData {
  store_name?: string;
  store_location?: string;
  receipt_date?: string;
  receipt_number?: string;
  transaction_number?: string;  // our POS txn (8-4); resolves real line items (Path A)
  customer_name?: string;
  items?: Array<{
    name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    is_red_bridge: boolean;
    bag_size_lbs?: number;
    feed_formula_id?: number;
    matched_product_name?: string;
    form_type?: string;
  }>;
  discounts?: Array<{
    description: string;
    amount: number;
    type?: string;
  }>;
  subtotal?: number;
  tax?: number;
  total?: number;
  original_total?: number;
  total_discounts?: number;
  red_bridge_total?: number;
  // Free bag detection
  free_bag_detection?: {
    has_free_bag_available: boolean;
    pending_rewards_count: number;
    detected_redemptions: Array<{
      discount_description: string;
      discount_amount: number;
      matched_item?: string;
      confidence: string;
    }>;
    auto_redeem_eligible: boolean;
    loyalty_progress: number;
  };
  // Bin matching
  bin_matches?: Array<{
    product_name: string;
    display_name?: string;
    total_lbs: number;
    is_red_bridge?: boolean;
    category?: string;
    matched_bin_id?: number;
    matched_bin_name?: string;
    match_confidence?: string;
    create_suggestion?: {
      suggested_name: string;
      form_type: string;
      initial_lbs: number;
      category?: string;
    };
  }>;
}

export const bridgeClubAPI = {
  // Auth
  register: async (data: RegisterData) => {
    const res = await fetch(`${API_BASE}/register/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  login: async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/login/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    return handleResponse(res);
  },

  logout: async () => {
    const res = await fetch(`${API_BASE}/logout/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  resendVerification: async (email: string) => {
    const res = await fetch(`${API_BASE}/resend-verification/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email })
    });
    return handleResponse(res);
  },

  forgotPassword: async (email: string) => {
    const res = await fetch(`${API_BASE}/forgot-password/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email })
    });
    return handleResponse(res);
  },

  resetPassword: async (token: string, password: string) => {
    const res = await fetch(`${API_BASE}/reset-password/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ token, password })
    });
    return handleResponse(res);
  },

  getMe: async () => {
    const res = await fetch(`${API_BASE}/me/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Mark the universal onboarding (animals + bins) complete. Idempotent, server-stamped.
  completeOnboarding: async () => {
    const res = await fetch(`${API_BASE}/me/complete-onboarding/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getDashboard: async () => {
    const res = await fetch(`${API_BASE}/dashboard/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Recover Past Purchases — local members linking shadow-profile
  // UnmatchedVisitor records to their account.
  getRecoverPurchasesPrompt: async () => {
    const res = await fetch(`${API_BASE}/me/recover-purchases-prompt/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  recoverPurchases: async (params: {
    transaction_number?: string;
    card_last_four?: string;
  }) => {
    const res = await fetch(`${API_BASE}/me/recover-purchases/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(params),
    });
    return handleResponse(res);
  },

  dismissRecoverPrompt: async () => {
    const res = await fetch(`${API_BASE}/me/dismiss-recover-prompt/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  updateProfile: async (data: Partial<RegisterData & {
    notifications_enabled?: boolean;
    feed_reminders_enabled?: boolean;
    weather_alerts_enabled?: boolean;
    reminder_days_before?: number;
    linked_store_id?: number;
  }>) => {
    const res = await fetch(`${API_BASE}/me/`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  // Stores
  getStores: async (params?: { city?: string; state?: string; zip?: string }) => {
    const query = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    const res = await fetch(`${API_BASE}/stores/${query ? `?${query}` : ''}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  linkStore: async (storeId: number) => {
    const res = await fetch(`${API_BASE}/link-store/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ store_id: storeId })
    });
    return handleResponse(res);
  },

  getMyStore: async () => {
    const res = await fetch(`${API_BASE}/my-store/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Animals
  getAnimals: async () => {
    const res = await fetch(`${API_BASE}/animals/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  addAnimal: async (data: AnimalData) => {
    const res = await fetch(`${API_BASE}/animals/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  getAnimal: async (id: number) => {
    const res = await fetch(`${API_BASE}/animals/${id}/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  updateAnimal: async (id: number, data: Partial<AnimalData>) => {
    const res = await fetch(`${API_BASE}/animals/${id}/`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  deleteAnimal: async (id: number) => {
    const res = await fetch(`${API_BASE}/animals/${id}/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    if (!res.ok && res.status !== 204) {
      throw await res.json();
    }
    return { success: true };
  },

  // Purchases
  getPurchases: async (params?: { start_date?: string; end_date?: string }) => {
    const query = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    const res = await fetch(`${API_BASE}/purchases/${query ? `?${query}` : ''}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  uploadReceipt: async (imageBase64: string, purchaseDate?: string) => {
    const res = await fetch(`${API_BASE}/purchases/upload-receipt/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ receipt_image: imageBase64, purchase_date: purchaseDate })
    });
    return handleResponse(res);
  },

  confirmPurchase: async (data: {
    extracted_data: ExtractedReceiptData;
    store_id?: number;
    purchase_date?: string;
    items?: ExtractedReceiptData['items'];
    receipt_image?: string;
    needs_verification?: boolean;
  }) => {
    const res = await fetch(`${API_BASE}/purchases/confirm/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  getPurchase: async (id: number) => {
    const res = await fetch(`${API_BASE}/purchases/${id}/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },
  // The member's own register receipt, as printed (read from the POS ledger).
  getPosReceipt: async (transactionId: number) => {
    const res = await fetch(`${API_BASE}/purchases/pos/${transactionId}/receipt/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Feed tracking
  getFeedBags: async () => {
    const res = await fetch(`${API_BASE}/feed-bags/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  updateFeedBag: async (id: number, data: {
    started_date?: string;
    finished_date?: string;
    is_active?: boolean;
    notes?: string;
    daily_consumption_lbs?: number;
    animal_id?: number | null;
  }) => {
    const res = await fetch(`${API_BASE}/feed-bags/${id}/`, {
      method: 'PATCH',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  refillFeedBag: async (id: number, amount?: number) => {
    const res = await fetch(`${API_BASE}/feed-bags/${id}/refill/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ amount })
    });
    return handleResponse(res);
  },

  finishFeedBag: async (id: number) => {
    const res = await fetch(`${API_BASE}/feed-bags/${id}/finish/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getFeedStatus: async () => {
    const res = await fetch(`${API_BASE}/feed-status/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Loyalty
  getLoyalty: async () => {
    const res = await fetch(`${API_BASE}/loyalty/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  redeemReward: async () => {
    const res = await fetch(`${API_BASE}/loyalty/redeem/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getRedemptions: async () => {
    const res = await fetch(`${API_BASE}/loyalty/redemptions/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Local points (Kettle Falls members) — same LocalPointsBalance source the
  // POS reads, so the member app and the till agree. Returns { local_points }
  // (null for non-local members). (Tax-exemption methods already live in the
  // TAX EXEMPTION section below.)
  getLocalPoints: async () => {
    const res = await fetch(`${API_BASE}/local-points/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Card
  getCard: async () => {
    const res = await fetch(`${API_BASE}/card/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Weather alerts
  getWeatherAlerts: async () => {
    const res = await fetch(`${API_BASE}/weather-alerts/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Weather forecast
  getWeatherForecast: async () => {
    const res = await fetch(`${API_BASE}/weather-forecast/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Detailed weather with hourly/daily forecast and animal-specific tips
  getWeatherDetailed: async () => {
    const res = await fetch(`${API_BASE}/weather-detailed/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Feed products
  getFeedProducts: async (params?: { animal_type?: string }) => {
    const query = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    const res = await fetch(`${API_BASE}/feed-products/${query ? `?${query}` : ''}`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // Feed Calculator
  getFeedCalculatorData: async () => {
    const res = await fetch(`${API_BASE}/feed-calculator/data/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  // ============ Feed Bins (new bin-based tracking) ============

  getFeedBins: async () => {
    const res = await fetch(`${API_BASE}/feed-bins/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  createFeedBin: async (data: {
    name: string;
    feed_category?: string;
    form_type?: string | null;
    low_alert_lbs?: number;
    critical_alert_lbs?: number;
  }) => {
    const res = await fetch(`${API_BASE}/feed-bins/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  getFeedBin: async (binId: number) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  updateFeedBin: async (binId: number, data: {
    name?: string;
    feed_category?: string;
    form_type?: string | null;
    low_alert_lbs?: number;
    critical_alert_lbs?: number;
    is_active?: boolean;
    feed_product_id?: number;   // inline "set the feed" — makes a bin deliverable
  }) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  deleteFeedBin: async (binId: number) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    if (!res.ok && res.status !== 204) {
      throw await res.json();
    }
    return { success: true };
  },

  addFeedToBin: async (binId: number, data: {
    lbs: number;
    product_name?: string;
    brand?: string;
    is_red_bridge?: boolean;
    notes?: string;
    form_type?: string;
    feed_product_id?: number;   // branded-catalog FeedProduct id; sets FeedBin.feed_product
  }) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/add-feed/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  adjustBinLevel: async (binId: number, data: { new_level: number; notes?: string }) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/adjust/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  getBinAnimals: async (binId: number) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/animals/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  linkAnimalToBin: async (binId: number, data: {
    animal_id: number;
    daily_consumption_lbs?: number;
  }) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/animals/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  updateAnimalLink: async (binId: number, linkId: number, data: { daily_consumption_lbs: number }) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/animals/${linkId}/`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  unlinkAnimalFromBin: async (binId: number, linkId: number) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/animals/${linkId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    if (!res.ok && res.status !== 204) {
      throw await res.json();
    }
    return { success: true };
  },

  updateBinAnimalFeeding: async (binId: number, linkId: number, data: {
    use_custom_amount: boolean;
    custom_amount_per_day?: number | null;
    custom_amount_unit?: string;
    scoop_size_oz?: number | null;
    feeding_notes?: string;
  }) => {
    const res = await fetch(`${API_BASE}/feed-bins/${binId}/animals/${linkId}/`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  // ============ Pending Feed Allocations ============

  getPendingAllocations: async () => {
    const res = await fetch(`${API_BASE}/pending-allocations/`, { headers: getHeaders(), credentials: 'include' });
    return handleResponse(res);
  },

  allocatePendingToBin: async (allocationId: number, binId: number) => {
    const res = await fetch(`${API_BASE}/pending-allocations/${allocationId}/allocate/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ bin_id: binId })
    });
    return handleResponse(res);
  },

  skipPendingAllocation: async (allocationId: number) => {
    const res = await fetch(`${API_BASE}/pending-allocations/${allocationId}/skip/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  createBinAndAllocate: async (allocationId: number, data: {
    bin_name?: string;
    feed_category?: string;
    low_alert_lbs?: number;
    critical_alert_lbs?: number;
  }) => {
    const res = await fetch(`${API_BASE}/pending-allocations/${allocationId}/create-bin/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  bulkAllocate: async (allocations: Array<{
    allocation_id: number;
    bin_id?: number;
    action?: 'allocate' | 'skip';
  }>) => {
    const res = await fetch(`${API_BASE}/pending-allocations/bulk/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ allocations })
    });
    return handleResponse(res);
  },

  // ============ Feed Products Database (Manufacturer Guidelines) ============

  // PAGINATED since the [:200] fix. Returns {count, total_pages, current_page,
  // page_size, next, previous, results}. `search` is now server-side: the
  // picker debounces into it rather than filtering a truncated client-side
  // list, which is what made the cap invisible.
  getFeedProductsDatabase: async (params?: {
    category?: string;
    brand?: string;
    source?: string;
    search?: string;
    include_user_added?: boolean;
    page?: number;
    page_size?: number;
  }) => {
    const query = params ? new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      )
    ).toString() : '';
    const res = await fetch(`${API_BASE}/feed-products-database/${query ? `?${query}` : ''}`, {
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },

  getFeedProductDetail: async (productId: number) => {
    const res = await fetch(`${API_BASE}/feed-products-database/${productId}/`, {
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },

  getFeedProductBrands: async (category?: string) => {
    const query = category ? `?category=${category}` : '';
    const res = await fetch(`${API_BASE}/feed-products-database/brands/${query}`, {
      headers: getHeaders(),
      credentials: 'include'
    });
    return handleResponse(res);
  },

  createCustomFeedProduct: async (data: {
    name: string;
    brand: string;
    animal_category: string;
    short_name?: string;
    life_stage?: string;
    breed_size?: string;
    special_diet?: string;
    bag_sizes?: number[];
    default_bag_lbs?: number;
    feeding_guidelines?: {
      type: 'weight_based' | 'per_head' | 'body_weight_pct';
      unit?: string;
      ranges?: Array<{ min_lbs: number; max_lbs: number; cups_per_day: number }>;
      daily_per_head?: number;
      percentage?: number;
    };
    cups_per_lb?: number;
  }) => {
    const res = await fetch(`${API_BASE}/feed-products-database/custom/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  calculateFeedConsumption: async (data: {
    product_id: number;
    weight_lbs?: number;
    count?: number;
    activity_level?: 'low' | 'normal' | 'high';
  }) => {
    const res = await fetch(`${API_BASE}/feed-products-database/calculate/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  // ============ Animal Growth & Tape Weight ============

  recalculateAnimalGrowth: async (animalId: number) => {
    const res = await fetch(`${API_BASE}/animals/${animalId}/recalculate-growth/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getTapeMeasurements: async (animalId: number) => {
    const res = await fetch(`${API_BASE}/animals/${animalId}/tape-measurements/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  addTapeMeasurement: async (animalId: number, data: {
    heart_girth_inches: number;
    body_length_inches?: number;
    formula_used?: 'standard' | 'simplified';
    notes?: string;
    update_animal_weight?: boolean;
  }) => {
    const res = await fetch(`${API_BASE}/animals/${animalId}/tape-measurements/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  calculateTapeWeight: async (data: {
    heart_girth_inches: number;
    body_length_inches?: number;
    formula?: 'auto' | 'standard' | 'simplified';
  }) => {
    const res = await fetch(`${API_BASE}/tools/tape-weight-calculator/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  // ============ Farm Challenge Rewards ============

  getFarmChallengeStatus: async () => {
    const res = await fetch(`${API_BASE}/farm-challenge/status/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  enrollInFarmChallenge: async () => {
    const res = await fetch(`${API_BASE}/farm-challenge/enroll/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getFarmChallengeConfig: async () => {
    const res = await fetch(`${API_BASE}/farm-challenge/config/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  logWeightForPoints: async (data: {
    animal_id: number;
    weight_lbs: number;
  }) => {
    // Backend expects 'weight' not 'weight_lbs'
    const payload = {
      animal_id: data.animal_id,
      weight: data.weight_lbs,
    };
    const res = await fetch(`${API_BASE}/farm-challenge/log-weight/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    return handleResponse(res);
  },

  checkWeightEligibility: async (animalId: number) => {
    const res = await fetch(`${API_BASE}/farm-challenge/check-weight/${animalId}/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  logHarvestForPoints: async (data: {
    animal_id: number;
    sale_weight?: number;
    sale_price?: number;
  }) => {
    const res = await fetch(`${API_BASE}/farm-challenge/log-harvest/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  logEggsForPoints: async (data: {
    animal_id?: number;
    egg_count: number;
  }) => {
    const res = await fetch(`${API_BASE}/farm-challenge/log-eggs/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  logMilkForPoints: async (data: {
    animal_id: number;
    gallons: number;
  }) => {
    const res = await fetch(`${API_BASE}/farm-challenge/log-milk/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  getFarmChallengeHistory: async (params?: { limit?: number }) => {
    const query = params ? new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      )
    ).toString() : '';
    const res = await fetch(`${API_BASE}/farm-challenge/history/${query ? `?${query}` : ''}`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getRedemptionTiers: async () => {
    const res = await fetch(`${API_BASE}/farm-challenge/redemption-tiers/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  createFarmChallengeRedemption: async (data: {
    tier_id: number;
  }) => {
    const res = await fetch(`${API_BASE}/farm-challenge/redeem/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },

  getFarmChallengeRedemptions: async () => {
    const res = await fetch(`${API_BASE}/farm-challenge/redemptions/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getFarmChallengeTransactions: async (params?: { limit?: number }) => {
    const query = params ? new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      )
    ).toString() : '';
    const res = await fetch(`${API_BASE}/farm-challenge/transactions/${query ? `?${query}` : ''}`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // ============ Member Feedback ============

  submitFeedback: async (data: { feedback_type: string; message: string; reply_email?: string }) => {
    const res = await fetch(`${API_BASE}/feedback/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  // ============ Profile Picture ============

  uploadProfilePicture: async (imageFile: File): Promise<{ success: boolean; profile_picture_url?: string }> => {
    const formData = new FormData();
    formData.append('image', imageFile);

    const res = await fetch(`${API_BASE}/profile/picture/`, {
      method: 'POST',
      credentials: 'include',
      // Don't set Content-Type header - browser will set it with boundary for FormData
      body: formData,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to upload image' }));
      throw new Error(error.error || 'Failed to upload image');
    }

    return res.json();
  },

  deleteProfilePicture: async (): Promise<{ success: boolean }> => {
    const res = await fetch(`${API_BASE}/profile/picture/delete/`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error('Failed to delete image');
    }

    return res.json();
  },

  // Welcome popup / Tutorial
  markWelcomeSeen: async (): Promise<{ success: boolean; welcome_popup_seen: boolean }> => {
    const res = await fetch(`${API_BASE}/welcome-seen/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  resetWelcomePopup: async (): Promise<{ success: boolean; welcome_popup_seen: boolean }> => {
    const res = await fetch(`${API_BASE}/welcome-reset/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Resources
  getResources: async () => {
    const res = await fetch(`${API_BASE}/resources/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getResourceFolder: async (slug: string) => {
    const res = await fetch(`${API_BASE}/resources/folder/${slug}/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  trackResourceDownload: async (resourceId: number) => {
    const res = await fetch(`${API_BASE_URL}/resources/${resourceId}/track-download/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // ============ Predator Identification Tool ============

  getPredators: async (params?: { category?: string; livestock_type?: string }) => {
    const query = params ? new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      )
    ).toString() : '';
    const res = await fetch(`${API_BASE_URL}/farmhand/predator-id/predators/${query ? `?${query}` : ''}`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getPreventionTerms: async () => {
    const res = await fetch(`${API_BASE_URL}/farmhand/predator-id/prevention_terms/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getEvidenceChecklist: async () => {
    const res = await fetch(`${API_BASE_URL}/farmhand/predator-id/evidence_checklist/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  analyzePredatorPhoto: async (data: {
    image: string;
    livestock_type: string;
    context?: string;
    additional_images?: string[];
  }) => {
    const res = await fetch(`${API_BASE_URL}/farmhand/predator-id/analyze_photo/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  identifyPredator: async (data: {
    livestock_type: string;
    evidence_list: string[];
    time_of_attack?: string;
    location?: string;
    image?: string;
    context?: string;
  }) => {
    const res = await fetch(`${API_BASE_URL}/farmhand/predator-id/identify/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  identifyPredatorMultiPhoto: async (data: {
    livestock_type: string;
    photos: Array<{ image_base64: string; photo_type: string }>;
    evidence_list?: string[];
    time_of_attack?: string;
    context?: string;
  }) => {
    const res = await fetch(`${API_BASE_URL}/farmhand/predator-id/identify_multi_photo/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  savePredatorIncident: async (data: {
    predator_type: string;
    livestock_type: string;
    count_lost?: number;
    incident_date?: string;
    time_of_day?: string;
    location?: string;
    evidence_list?: string[];
    notes?: string;
    photo_url?: string;
    photos?: Array<{ image_base64: string; photo_type: string; filename?: string }>;
    batch_id?: number;
    zone_id?: number;
  }) => {
    const res = await fetch(`${API_BASE_URL}/farmhand/predator-id/save_incident/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  getPredatorHistory: async () => {
    const res = await fetch(`${API_BASE_URL}/farmhand/predator-id/farm_history/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getPredatorIncidents: async (limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    const res = await fetch(`${API_BASE}/predator-id/incidents/${query}`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  deletePredatorIncident: async (incidentId: number) => {
    const res = await fetch(`${API_BASE}/predator-id/incidents/${incidentId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    if (!res.ok && res.status !== 204) {
      throw await res.json();
    }
    return { success: true };
  },

  // ============ Flock Health Assistant ============

  getHealthOptions: async () => {
    const res = await fetch(`${API_BASE}/health-assistant/options/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  analyzeMortality: async (data: {
    signs: string[];
    bird_info?: { age_weeks?: number; type?: string; breed?: string };
    flock_size?: number;
  }) => {
    const res = await fetch(`${API_BASE}/health-assistant/analyze-mortality/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  analyzeDroppings: async (data: {
    tags: string[];
    additional_info?: Record<string, unknown>;
  }) => {
    const res = await fetch(`${API_BASE}/health-assistant/analyze-droppings/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  checkSymptoms: async (data: {
    symptoms: string[];
    bird_info?: { age_weeks?: number; type?: string; breed?: string };
    flock_size?: number;
  }) => {
    const res = await fetch(`${API_BASE}/health-assistant/check-symptoms/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  getComprehensiveAnalysis: async (data: {
    analysis_type: 'mortality' | 'droppings' | 'symptoms';
    data: Record<string, unknown>;
    flock_size?: number;
  }) => {
    const res = await fetch(`${API_BASE}/health-assistant/comprehensive/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  getWaterAlerts: async (params?: { flock_size?: number; is_laying?: boolean }) => {
    const query = params ? new URLSearchParams(
      Object.entries(params).reduce((acc, [k, v]) => ({ ...acc, [k]: String(v) }), {})
    ).toString() : '';
    const res = await fetch(`${API_BASE}/health-assistant/water-alerts/${query ? `?${query}` : ''}`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // ============================================
  // Local Access (Mill Store) APIs
  // ============================================

  // Categories (fetched from backend, excludes BYOC)
  getLocalCategories: async () => {
    const res = await fetch(`${API_BASE}/local/categories/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Products
  getLocalProducts: async (category?: string, search?: string) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    const query = params.toString();
    const res = await fetch(`${API_BASE}/local/products/${query ? `?${query}` : ''}`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getLocalProduct: async (productId: number) => {
    const res = await fetch(`${API_BASE}/local/products/${productId}/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getBYOCStock: async () => {
    const res = await fetch(`${API_BASE}/local/byoc-stock/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Cart
  getLocalCart: async () => {
    const res = await fetch(`${API_BASE}/local/cart/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  addToLocalCart: async (productId: number, quantity: number = 1) => {
    const res = await fetch(`${API_BASE}/local/cart/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ product_id: productId, quantity }),
    });
    return handleResponse(res);
  },

  // Add a product-less tab-payment line to the cart. Checks out through the same
  // Helcim store flow as feed (we don't vault cards); on paid the backend writes
  // a TAB_PAYMENT to the ledger. Replaces the retired standalone saved-card pay().
  addTabPaymentToCart: async (amount: number) => {
    const res = await fetch(`${API_BASE}/local/cart/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ line_type: 'tab_payment', amount }),
    });
    return handleResponse(res);
  },

  updateLocalCartItem: async (itemId: number, quantity: number) => {
    const res = await fetch(`${API_BASE}/local/cart/items/${itemId}/`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ quantity }),
    });
    return handleResponse(res);
  },

  removeLocalCartItem: async (itemId: number) => {
    const res = await fetch(`${API_BASE}/local/cart/items/${itemId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  clearLocalCart: async () => {
    const res = await fetch(`${API_BASE}/local/cart/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Orders
  getLocalOrders: async () => {
    const res = await fetch(`${API_BASE}/local/orders/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getLocalOrder: async (orderId: number) => {
    const res = await fetch(`${API_BASE}/local/orders/${orderId}/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  retryPaymentLink: async (orderId: number) => {
    const res = await fetch(`${API_BASE}/local/orders/${orderId}/retry-payment/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  recordHelcimPayment: async (
    orderId: number,
    payload: { transaction_id: string; amount?: number | string },
  ) => {
    const res = await fetch(
      `${API_BASE}/local/orders/${orderId}/record-helcim-payment/`,
      {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      },
    );
    return handleResponse(res);
  },

  // On-demand trigger for the Gmail-parser run. Returns quickly with
  // the order's current paid state. Idempotent — backend dedupes via
  // BridgeClubOrder.helcim_transaction_id partial-unique. Used by the
  // post-checkout poll on LocalOrderDetail to shorten the "confirming"
  // window from 5 min (scheduled cadence) to seconds when the receipt
  // has already landed in the inbox.
  checkPaymentNow: async (orderId: number) => {
    const res = await fetch(
      `${API_BASE}/local/orders/${orderId}/check-payment-now/`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
      },
    );
    return handleResponse(res);
  },

  createLocalOrder: async (options: {
    pickupMethod: 'in_store' | 'locker';
    notes?: string;
    requestedPickupDate?: string;
    paymentMethod?: 'card' | 'ach' | 'cash' | 'invoice';
    savedPaymentId?: number;
  }) => {
    const res = await fetch(`${API_BASE}/local/orders/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        pickup_method: options.pickupMethod,
        notes: options.notes || '',
        requested_pickup_date: options.requestedPickupDate,
        payment_method: options.paymentMethod || 'card',
        saved_payment_id: options.savedPaymentId,
      }),
    });
    return handleResponse(res);
  },

  // Access Code
  validateAccessCode: async (code: string) => {
    const res = await fetch(`${API_BASE}/validate-access-code/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    return handleResponse(res);
  },

  applyAccessCode: async (code: string) => {
    const res = await fetch(`${API_BASE}/apply-access-code/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    return handleResponse(res);
  },

  // ============ Linked Store Accounts ============

  getLinkedStores: async () => {
    const res = await fetch(`${API_BASE}/profile/linked-stores/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getAvailableStores: async () => {
    const res = await fetch(`${API_BASE}/profile/available-stores/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  linkPartnerStore: async (retailPartnerId: number, partnerLoyaltyId: string) => {
    const res = await fetch(`${API_BASE}/profile/link-store/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        retail_partner_id: retailPartnerId,
        partner_loyalty_id: partnerLoyaltyId,
      }),
    });
    return handleResponse(res);
  },

  unlinkStore: async (linkId: number) => {
    const res = await fetch(`${API_BASE}/profile/linked-stores/${linkId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // ============ Scan & Go Self-Checkout ============

  // Lookup product by barcode (UPC or SKU)
  scanAndGoLookupBarcode: async (barcode: string) => {
    const res = await fetch(`${API_BASE}/scan-and-go/lookup-barcode/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ barcode }),
    });
    return handleResponse(res);
  },

  // Lookup BYOC bin by QR code
  scanAndGoLookupBYOCBin: async (binQrCode: string) => {
    const res = await fetch(`${API_BASE}/scan-and-go/lookup-byoc-bin/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ bin_qr_code: binQrCode }),
    });
    return handleResponse(res);
  },

  // Get current Scan & Go session (or create new one)
  getScanAndGoSession: async () => {
    const res = await fetch(`${API_BASE}/scan-and-go/session/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Add item to Scan & Go session
  addToScanAndGoSession: async (productId: number, quantity: number = 1, isWeighted: boolean = false, weightLbs?: number) => {
    const res = await fetch(`${API_BASE}/scan-and-go/session/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        product_id: productId,
        quantity,
        is_weighted: isWeighted,
        weight_lbs: weightLbs,
      }),
    });
    return handleResponse(res);
  },

  // Update item quantity in Scan & Go session
  updateScanAndGoItem: async (itemId: number, quantity: number) => {
    const res = await fetch(`${API_BASE}/scan-and-go/session/items/${itemId}/`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ quantity }),
    });
    return handleResponse(res);
  },

  // Remove item from Scan & Go session
  removeScanAndGoItem: async (itemId: number) => {
    const res = await fetch(`${API_BASE}/scan-and-go/session/items/${itemId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Get saved payment methods
  // A member's OWN wallet, not a Scan & Go feature. This used to call
  // scan-and-go/saved-payment-methods/, which is gated on local_access -
  // so every Treasure Valley delivery subscriber got a 403 and an empty
  // wallet while holding a card that was charging their subscription. The
  // endpoint is the same view; only the path and the gate changed.
  getSavedPaymentMethods: async () => {
    const res = await fetch(`${API_BASE}/payment-methods/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Delete saved payment method (soft-delete server-side). Returns the refreshed
  // list, which reflects any auto-promoted default.
  deleteSavedPaymentMethod: async (methodId: number) => {
    const res = await fetch(`${API_BASE}/payment-methods/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ payment_method_id: methodId }),
    });
    return handleResponse(res);
  },

  // Set a saved payment method as the default. Returns the refreshed list.
  setDefaultPaymentMethod: async (methodId: number) => {
    const res = await fetch(`${API_BASE}/payment-methods/`, {
      method: 'PATCH',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ payment_method_id: methodId }),
    });
    return handleResponse(res);
  },

  // Add a card (HelcimPay verify vault) — step 1: mint a checkout token.
  addCardInitialize: async () => {
    const res = await fetch(`${API_BASE}/payment-methods/initialize/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Add a card — step 2: forward the EXACT signed HelcimPay response plus the
  // checkout_token. Do NOT reshape `response` — any mutation breaks the hash.
  addCardConfirm: async (payload: { checkout_token: string; response: any }) => {
    const res = await fetch(`${API_BASE}/payment-methods/confirm/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    return handleResponse(res);
  },

  // Checkout Scan & Go session
  scanAndGoCheckout: async (paymentMethodId?: number, saveCard: boolean = false) => {
    const res = await fetch(`${API_BASE}/scan-and-go/checkout/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        saved_payment_method_id: paymentMethodId,
        save_card: saveCard,
      }),
    });
    return handleResponse(res);
  },

  // Get Scan & Go order details by session ID
  getScanAndGoOrder: async (sessionId: string) => {
    const res = await fetch(`${API_BASE}/scan-and-go/order/${sessionId}/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Mark Scan & Go tutorial as complete
  completeScanAndGoTutorial: async () => {
    const res = await fetch(`${API_BASE}/scan-and-go/tutorial-complete/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // ============================================
  // Mill Store - Online Ordering
  // ============================================

  millStoreCatalog: async (params?: { category?: number; q?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.append('category', params.category.toString());
    if (params?.q) searchParams.append('q', params.q);
    const queryString = searchParams.toString();
    const res = await fetch(`${API_BASE}/mill-store/catalog/${queryString ? '?' + queryString : ''}`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  millStoreCategories: async () => {
    const res = await fetch(`${API_BASE}/mill-store/categories/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  millStoreCart: async () => {
    const res = await fetch(`${API_BASE}/mill-store/cart/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  millStoreAddToCart: async (productId: number, quantity: number = 1) => {
    const res = await fetch(`${API_BASE}/mill-store/cart/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ product_id: productId, quantity }),
    });
    return handleResponse(res);
  },

  millStoreUpdateCartItem: async (itemId: number, quantity: number) => {
    const res = await fetch(`${API_BASE}/mill-store/cart/items/${itemId}/`, {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ quantity }),
    });
    return handleResponse(res);
  },

  millStoreRemoveCartItem: async (itemId: number) => {
    const res = await fetch(`${API_BASE}/mill-store/cart/items/${itemId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  millStoreClearCart: async () => {
    const res = await fetch(`${API_BASE}/mill-store/cart/clear/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  millStoreCheckout: async (paymentMethodId: number) => {
    const res = await fetch(`${API_BASE}/mill-store/checkout/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ payment_method_id: paymentMethodId }),
    });
    return handleResponse(res);
  },

  millStoreOrders: async () => {
    const res = await fetch(`${API_BASE}/mill-store/orders/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  millStoreOrderDetail: async (orderNumber: string) => {
    const res = await fetch(`${API_BASE}/mill-store/orders/${orderNumber}/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // ============================================
  // Bulk Pre-Order (Custom Feed Orders)
  // ============================================

  // Get livestock categories for bulk ordering
  getBulkFeedCategories: async () => {
    const res = await fetch(`${API_BASE}/local/bulk-feeds/categories/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Get bulk feeds available for pre-order
  getBulkFeeds: async (params?: { animal_type?: string; feed_grade?: string; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.animal_type) searchParams.append('animal_type', params.animal_type);
    if (params?.feed_grade) searchParams.append('feed_grade', params.feed_grade);
    if (params?.search) searchParams.append('search', params.search);
    const queryString = searchParams.toString();
    const res = await fetch(`${API_BASE}/local/bulk-feeds/${queryString ? '?' + queryString : ''}`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Get details of a specific bulk feed formula
  getBulkFeedDetail: async (formulaId: number) => {
    const res = await fetch(`${API_BASE}/local/bulk-feeds/${formulaId}/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Get bulk order requirements (min pickup date, payment methods, etc.)
  getBulkOrderRequirements: async () => {
    const res = await fetch(`${API_BASE}/local/bulk-orders/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Create bulk pre-order
  createBulkOrder: async (data: {
    items: Array<{
      formula_id: number;
      quantity_lbs: number;
      form: 'MASH' | 'PELLET';
    }>;
    payment_method: 'card' | 'ach';
    requested_pickup_date: string;
    notes?: string;
  }) => {
    const res = await fetch(`${API_BASE}/local/bulk-orders/`, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  // Get list of bulk pre-orders
  getBulkOrders: async () => {
    const res = await fetch(`${API_BASE}/local/bulk-orders/list/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Get details of a specific bulk pre-order
  getBulkOrderDetail: async (orderNumber: string) => {
    const res = await fetch(`${API_BASE}/local/bulk-orders/${orderNumber}/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // =============================================
  // TAX EXEMPTION
  // =============================================

  // Get tax exemption status
  // Member-facing account (Phase 5): view credit/bags/tab + pay tab.
  getMemberAccount: async () => {
    const res = await fetch(`${API_BASE}/local/account/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  getTaxExemptionStatus: async () => {
    const res = await fetch(`${API_BASE}/local/tax-exemption/status/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },

  // Submit tax exemption request
  submitTaxExemption: async (formData: FormData) => {
    const res = await fetch(`${API_BASE}/local/tax-exemption/submit/`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    return handleResponse(res);
  },

  // Get tax exemption request history
  getTaxExemptionHistory: async () => {
    const res = await fetch(`${API_BASE}/local/tax-exemption/history/`, {
      headers: getHeaders(),
      credentials: 'include',
    });
    return handleResponse(res);
  },
};
